import { current, produce } from 'immer'

import {
  extract_author,
  extract_end,
  extract_log,
  extract_message,
  extract_next_steps,
  extract_prompts,
} from './stream.handler'

import {
  GenerateProcessFromDocumentResponse,
  GenerateProcessResponse,
} from '../../../agents/analyst/api/analyst.api'
import { AiAnalystUtils, AiProcessEntities } from '../../../agents/analyst/services/analyst.service'
import { AiAnalystActions } from '../../../agents/analyst/store/analyst.actions'
import { AiAgent, Message, MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

export const onExtractProcessFromDocumentSuccess = (
  state: AiState,
  { response }: { response: GenerateProcessFromDocumentResponse }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: `<b>Look what I found in the selected document:</b> <p> <i>${response.response}</i> </p> I will use this information to build/modify the bussines process for you.`,
      type: MessageType.AiMsg,
      commited: true,
    })
    AiAnalystActions.generateProcess({ prompt: response.response })
  })

export const onGenerateProcessSuccess = (
  state: AiState,
  { response, aiEntities }: { response: GenerateProcessResponse; aiEntities: AiProcessEntities }
): AiState =>
  produce(state, (draft: AiState) => {
    if (response.answer) {
      const processStateSnapshot = AiAnalystUtils.getProcessStateSnapshot()
      const changes = AiAnalystUtils.getProcessChanges(
        aiEntities,
        processStateSnapshot.nodes,
        processStateSnapshot.boundaryEvents
      )
      draft.selectedAgent = AiAgent.ANALYST
      draft.generatedProcess = response.answer
      draft.generatedProcessEntities = aiEntities
      if (!response.question) {
        draft.messages.push({
          message: response.answer.md,
          context: {
            ...response,
            changes,
          },
          type: MessageType.AiMsg,
        })
      }
    } else if (response.chat) {
      draft.messages.push({
        message: response.chat,
        type: MessageType.SystemMsg,
      })
    } else {
      draft.messages.push(generateChatErrorMessage(response.error_message))
    }
  })

export const onGetAnswerEventStream = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }
    const { message, context, sources } = response
    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const answer = extract_message(message)
    const nextSteps = extract_next_steps(message)
    const end = extract_end(message)
    const log = extract_log(message)
    const prompts = extract_prompts(message)
    const author = extract_author(message)

    if (message.answer?.operation === 'session') {
      draft.sessionId = message.answer.thread_id
      if (message.answer.request_id) {
        draft.lastRequestId = message.answer.request_id
      }
    }
    // parse logs and register
    if (log) {
      draft.logs.unshift({
        agent: log.agent,
        message: log.debug.info,
        message_type: 'INFO',
        timestamp: log.timestamp,
        status: log.debug.status,
        step: log.debug.step,
      })
      draft.currentAITaskMessage = log.debug.info
      return
    }
    if ((message as any).answer) {
      parseAnswerMessage(draft, message)
    }
    if (end) {
      parseEndMessage(draft, message)
    }
    if (!answer && !nextSteps) {
      return
    }
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    if (isLastMessageOfTypeAi) {
      Object.assign(lastMessageDraft, response)
      lastMessageDraft.message = nextSteps + answer
      lastMessageDraft.context = null
      lastMessageDraft.sources = sources
      lastMessageDraft.author = author
      lastMessageDraft.html_preview = null
      lastMessageDraft.prompts = prompts
      lastMessageDraft.type = MessageType.AiMsg
    } else {
      draft.messages.push({
        ...response,
        message,
        context,
        sources,
        type: MessageType.AiMsg,
      })
    }
  })

function parseAnswerMessage(draft: AiState, messagePayload): void {
  const payload = messagePayload.end
    ? { ai_analyst: messagePayload.end.answer }
    : messagePayload.answer
  const final = messagePayload.end && messagePayload.end.operation === 'link_process' ? true : false
  generateFromProcess(draft, payload, final)
}

function parseEndMessage(draft: AiState, message): void {
  const messagePayload = message.end
  if (messagePayload.operation === 'extract_sections') {
    draft.temporaryUI = {
      interface: messagePayload.answer,
      sectionsDetails: messagePayload.answer.sc.map((section) => ({ sc: section, rs: [] })) || [],
    }
  }

  if (messagePayload.operation === 'ai_classify') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'CLASSIFY IMAGE',
    })
  }
  if (messagePayload.operation === 'check_greeting') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'CHECK USER PROMPT',
    })
  }
  if (messagePayload.operation === 'process_reasoning') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'PROCESS REASONING',
    })
  }
  if (messagePayload.operation === 'initial_actions') {
    draft.messages[draft.messages.length - 1].commited = true
  }

  if (messagePayload.operation === 'ai_analyst') {
    parseAnswerMessage(draft, message)
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'PROCESS GENERATION',
    })
  }
  if (messagePayload.operation === 'link_process') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'LINK PROCESS',
    })
    parseAnswerMessage(draft, message)
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.pendingRouteToAnalyst = null
    draft.currentAITaskMessage = null
    draft.messages[draft.messages.length - 1].message = message.end.answer.md
    draft.messages[draft.messages.length - 1].type = MessageType.AiMsg
    draft.messages[draft.messages.length - 1].commited = false
  }
}

function generateFromProcess(draft: AiState, messagePayload, final: boolean = false): void {
  if (messagePayload?.ai_analyst) {
    let aiEntities: AiProcessEntities
    try {
      let payload = {}
      payload = { answer: messagePayload?.ai_analyst }

      aiEntities = AiAnalystUtils.extractAiEntitiesFromResponse(
        payload as any,
        AiAnalystUtils.getProcessStateSnapshot()
      )

      if (aiEntities) {
        if (final) {
          draft.generatedProcess = payload as any
          draft.generatedProcessEntities = aiEntities
        } else {
          draft.temporaryProcess = payload as any
          draft.temporaryProcessEntities = aiEntities
        }
      }
    } catch (e) {
      if (!messagePayload?.ai_analyst.chat) {
        console.error('I couldn’t generate a valid response. Please try again.', e)
      }
    }
  }
}

// Helpers functions - maybe common for all agents

function generateChatErrorMessage(errorMsg, statusCode: number = 400): Message {
  const message = extractMessageFromError(errorMsg, statusCode)

  return {
    message,
    type: MessageType.ErrorMsg,
    actions: {
      startNewSession: true,
    },
    commited: true,
  }
}

function extractMessageFromError(
  error:
    | string
    | { title: string; defaultMessage: string; error_message: { client_message: string } },
  statusCode: number = 400
): string {
  const CONTINUE_MESSAGE = `You can continue the conversation or start a new chat.`
  if (statusCode >= 500 || statusCode === 0) {
    return `Something went wrong on our end. ${CONTINUE_MESSAGE}`
  } else {
    const DEFAULT_MESSAGE = `Something went wrong with your request. Please check the information you entered and try again.`

    if (typeof error === 'string') {
      return `${error} ${CONTINUE_MESSAGE}`
    }

    const serverMessage =
      error?.title ?? error?.defaultMessage ?? error?.error_message?.client_message

    return `${serverMessage ?? DEFAULT_MESSAGE} ${CONTINUE_MESSAGE}`
  }
}
